import React from "react";
import { useRouteError, useNavigate } from "react-router";
import { 
    Box, 
    Typography, 
    Button, 
    Container, 
    Paper,
    useTheme,
    alpha,
    Stack
} from "@mui/material";
import { RiHome5Line, RiArrowLeftLine, RiErrorWarningLine, RiSearchLine } from "@remixicon/react";
import { Routes } from "../types/enums/Routes.enum";

interface NotFoundProps {
    title?: string;
    message?: string;
    showBackButton?: boolean;
    showHomeButton?: boolean;
    isErrorBoundary?: boolean;
}

const NotFound: React.FC<NotFoundProps> = ({
    title = "Página no encontrada",
    message = "Lo sentimos, la página que buscas no existe o ha sido movida.",
    showBackButton = true,
    showHomeButton = true,
    isErrorBoundary = false
}) => {
    const error: any = useRouteError();
    const navigate = useNavigate();
    const theme = useTheme();

    if (error) {
        console.error('Router Error:', error);
    }

    const isServerError = error?.status >= 500;
    const isClientError = error?.status >= 400 && error?.status < 500;

    const isChunkError = error?.name === "ChunkLoadError" 
                        || error?.message?.includes("chunk") 
                        || error?.status === 404 && !error?.data;
    
    //Hard Reload
    const handleGoHome = () => {
        // Forzar al navegador a pedir el index.html de nuevo
        window.location.href = '/';
    };

    const handleGoBack = () => {
        if (isChunkError) {
            window.location.reload();
        } else if (window.history.length > 1) {
            window.history.back();
        } else {
            handleGoHome();
        }
    };
    
    /* Determinar icono y colores según el tipo de error */
    const getErrorConfig = () => {
        if (isChunkError) {
            return {
                icon: RiErrorWarningLine,
                color: theme.palette.info.main,
                title: "Actualización Necesaria",
                message: "La aplicación se ha actualizado. Para continuar, haz clic en el botón de abajo."
            };
        }
        if (isServerError) {
            return {
                icon: RiErrorWarningLine,
                color: theme.palette.error.main,
                title: "Error del Servidor",
                message: "Ha ocurrido un error interno. Por favor, inténtalo más tarde."
            };
        }
        if (isClientError) {
            return {
                icon: RiSearchLine,
                color: theme.palette.warning.main,
                title: "Página no encontrada",
                message: "La página que buscas no existe o ha sido movida."
            };
        }
        return {
            icon: RiSearchLine,
            color: theme.palette.primary.main,
            title,
            message
        };
    };

    const errorConfig = getErrorConfig();
    const IconComponent = errorConfig.icon;

    return (
        <Box
            sx={{
                minHeight: '100vh',
                maxHeight: '100vh',
                overflow: 'hidden',
                background: `
                    radial-gradient(ellipse at top, ${alpha(errorConfig.color, 0.08)} 0%, transparent 50%),
                    radial-gradient(ellipse at bottom, ${alpha(theme.palette.primary.main, 0.05)} 0%, transparent 50%),
                    linear-gradient(135deg, ${alpha(theme.palette.background.default, 0.95)} 0%, #ffffff 100%)
                `,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: { xs: 2, md: 3 },
                position: 'relative',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `
                        radial-gradient(circle at 20% 20%, ${alpha(errorConfig.color, 0.03)} 0%, transparent 50%),
                        radial-gradient(circle at 80% 80%, ${alpha(theme.palette.primary.main, 0.03)} 0%, transparent 50%)
                    `,
                    pointerEvents: 'none',
                }
            }}
        >
            <Container maxWidth="md" sx={{ zIndex: 1 }}>
                <Paper
                    elevation={0}
                    sx={{
                        textAlign: 'center',
                        padding: { xs: 4, sm: 5, md: 6 },
                        borderRadius: 4,
                        background: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid',
                        borderColor: alpha(errorConfig.color, 0.08),
                        boxShadow: `
                            0 25px 50px -12px ${alpha(errorConfig.color, 0.08)},
                            0 0 0 1px ${alpha(errorConfig.color, 0.05)}
                        `,
                        position: 'relative',
                        overflow: 'hidden',
                        maxHeight: '90vh',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 3,
                            background: `linear-gradient(90deg, 
                                ${errorConfig.color} 0%, 
                                ${alpha(errorConfig.color, 0.7)} 50%, 
                                ${errorConfig.color} 100%
                            )`,
                        }
                    }}
                >
                    {/* Icono animado */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            mb: 3,
                            position: 'relative',
                        }}
                    >
                        <Box
                            sx={{
                                position: 'relative',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: { xs: 100, md: 120 },
                                height: { xs: 100, md: 120 },
                                borderRadius: '50%',
                                background: `radial-gradient(circle, ${alpha(errorConfig.color, 0.08)} 0%, ${alpha(errorConfig.color, 0.02)} 70%)`,
                                border: `2px solid ${alpha(errorConfig.color, 0.1)}`,
                                animation: 'gentlePulse 3s ease-in-out infinite',
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    width: '120%',
                                    height: '120%',
                                    borderRadius: '50%',
                                    background: `radial-gradient(circle, ${alpha(errorConfig.color, 0.03)} 0%, transparent 70%)`,
                                    animation: 'ripple 2s ease-in-out infinite',
                                }
                            }}
                        >
                            <IconComponent
                                size={60}
                                color={errorConfig.color}
                                style={{
                                    filter: 'drop-shadow(0px 4px 8px rgba(0,0,0,0.1))',
                                    zIndex: 1,
                                }}
                            />
                        </Box>
                        <style>
                            {`
                                @keyframes gentlePulse {
                                    0%, 100% { transform: scale(1); }
                                    50% { transform: scale(1.02); }
                                }
                                @keyframes ripple {
                                    0% { transform: scale(1); opacity: 0.3; }
                                    100% { transform: scale(1.4); opacity: 0; }
                                }
                            `}
                        </style>
                    </Box>

                    {/* Código de estado si es error HTTP */}
                    {error?.status && (
                        <Typography
                            variant="h1"
                            sx={{
                                fontSize: { xs: '3.5rem', md: '5rem' },
                                fontWeight: 800,
                                background: `linear-gradient(135deg, ${errorConfig.color} 0%, ${alpha(errorConfig.color, 0.7)} 100%)`,
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                color: 'transparent',
                                mb: 1,
                                fontFamily: '"Inter", "SF Pro Display", -apple-system, sans-serif',
                                letterSpacing: '-0.02em',
                            }}
                        >
                            {error.status}
                        </Typography>
                    )}

                    {/* Título principal */}
                    <Typography
                        variant="h3"
                        component="h1"
                        sx={{
                            fontWeight: 700,
                            color: theme.palette.grey[800],
                            mb: 2,
                            fontFamily: '"Inter", "SF Pro Display", -apple-system, sans-serif',
                            fontSize: { xs: '1.75rem', md: '2.5rem' },
                            letterSpacing: '-0.01em',
                        }}
                    >
                        {errorConfig.title}
                    </Typography>

                    {/* Descripción */}
                    <Typography
                        variant="body1"
                        sx={{
                            color: theme.palette.grey[600],
                            mb: 4,
                            maxWidth: 560,
                            mx: 'auto',
                            lineHeight: 1.7,
                            fontSize: '1.1rem',
                        }}
                    >
                        {errorConfig.message}
                        {!isErrorBoundary && (
                            <> Verifica la URL o regresa a la página principal.</>
                        )}
                    </Typography>

                    {/* Botones de acción */}
                    <Stack 
                        direction={{ xs: 'column', sm: 'row' }} 
                        spacing={2} 
                        justifyContent="center"
                        sx={{ mt: 4 }}
                    >
                        {showHomeButton && (
                            <Button
                                variant="contained"
                                startIcon={<RiHome5Line size={20} />}
                                onClick={handleGoHome}
                                sx={{
                                    paddingX: 4,
                                    paddingY: 1.5,
                                    borderRadius: 3,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '1rem',
                                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.24)}`,
                                    border: 'none',
                                    '&:hover': {
                                        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                                        boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.32)}`,
                                        transform: 'translateY(-2px)',
                                    },
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                }}
                            >
                                Ir al Inicio
                            </Button>
                        )}

                        {showBackButton && (
                            <Button
                                variant="outlined"
                                startIcon={<RiArrowLeftLine size={20} />}
                                onClick={handleGoBack}
                                sx={{
                                    paddingX: 4,
                                    paddingY: 1.5,
                                    borderRadius: 3,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '1rem',
                                    borderColor: alpha(theme.palette.grey[400], 0.8),
                                    color: theme.palette.grey[700],
                                    backgroundColor: 'transparent',
                                    '&:hover': {
                                        backgroundColor: alpha(theme.palette.grey[50], 0.8),
                                        borderColor: theme.palette.grey[400],
                                        transform: 'translateY(-2px)',
                                        boxShadow: `0 8px 24px ${alpha(theme.palette.grey[500], 0.12)}`,
                                    },
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                }}
                            >
                                Volver Atrás
                            </Button>
                        )}
                    </Stack>

                    {/* Información técnica para desarrollo */}
                    {(process.env.NODE_ENV === 'development' || isErrorBoundary) && error && (
                        <Box
                            sx={{
                                mt: 4,
                                p: 3,
                                backgroundColor: alpha(theme.palette.error.main, 0.04),
                                borderRadius: 3,
                                border: '1px solid',
                                borderColor: alpha(theme.palette.error.main, 0.12),
                                textAlign: 'left',
                                maxHeight: '200px',
                                overflow: 'auto',
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    color: theme.palette.error.main,
                                    fontFamily: '"JetBrains Mono", "SF Mono", Monaco, monospace',
                                    fontSize: '0.875rem',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                }}
                            >
                                {`Error Details:
                                Status: ${error.status || 'Unknown'}
                                Message: ${error.statusText || error.message || 'No additional information'}
                                Stack: ${error.stack ? error.stack.substring(0, 300) + '...' : 'Not available'}`}
                            </Typography>
                        </Box>
                    )}
                </Paper>
            </Container>
        </Box>
    );
}

export default NotFound;